import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { evaluateClientPolicy } from '../../auth/policies.js';
import { loginPath } from '../../lib/login-redirect.js';

interface ProtectedRouteProps {
  policy: string;
  redirectTo?: string;
  children: React.ReactNode;
}

/**
 * Route guard that redirects when the user lacks a required policy.
 *
 * Usage:
 *   <Route path="/trading" element={
 *     <ProtectedRoute policy="CanTrade"><TradingPage /></ProtectedRoute>
 *   } />
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ policy, redirectTo = '/', children }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const permissions = useAuthStore((s) => s.user?.permissions ?? []);
  const organizationPlan = useAuthStore((s) => s.user?.organizationPlan ?? null);
  const location = useLocation();

  // Not authenticated — redirect to login (not to /)
  if (!isAuthenticated) {
    return <Navigate to={loginPath(location)} replace />;
  }

  // Authenticated but lacks policy — redirect to home
  const allowed = evaluateClientPolicy(policy, permissions, organizationPlan);
  if (!allowed) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};
